import type { MonthWeek } from '@components/calendar/types';
import {
	type Locale,
	addDays,
	addWeeks,
	differenceInCalendarDays,
	endOfISOWeek,
	endOfMonth,
	endOfWeek,
	getISOWeek,
	getWeek,
	getWeeksInMonth,
	startOfISOWeek,
	startOfMonth,
	startOfWeek,
} from 'date-fns';

function daysToMonthWeeks(
	fromDate: Date,
	toDate: Date,
	options?: {
		ISOWeek?: boolean;
		locale?: Locale;
		weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
		firstWeekContainsDate?: 1 | 2 | 3 | 4 | 5 | 6 | 7;
	},
): MonthWeek[] {
	const toWeek = options?.ISOWeek
		? endOfISOWeek(toDate)
		: endOfWeek(toDate, options);
	const fromWeek = options?.ISOWeek
		? startOfISOWeek(fromDate)
		: startOfWeek(fromDate, options);

	const nOfDays = differenceInCalendarDays(toWeek, fromWeek);
	const days: Date[] = [];

	for (let i = 0; i <= nOfDays; i++) {
		days.push(addDays(fromWeek, i));
	}

	return days.reduce((result: MonthWeek[], date) => {
		const weekNumber = options?.ISOWeek
			? getISOWeek(date)
			: getWeek(date, options);

		const existingWeek = result.find(
			(value) => value.weekNumber === weekNumber,
		);
		if (existingWeek) {
			existingWeek.dates.push(date);
			return result;
		}
		result.push({
			weekNumber,
			dates: [date],
		});
		return result;
	}, []);
}

export function getMonthWeeks(
	month: Date,
	options: {
		locale: Locale;
		useFixedWeeks?: boolean;
		weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
		firstWeekContainsDate?: 1 | 2 | 3 | 4 | 5 | 6 | 7;
		ISOWeek?: boolean;
	},
): MonthWeek[] {
	const weeksInMonth: MonthWeek[] = daysToMonthWeeks(
		startOfMonth(month),
		endOfMonth(month),
		options,
	);

	if (options?.useFixedWeeks) {
		// Add extra weeks to the month, up to 6 weeks
		const nrOfMonthWeeks = getWeeksInMonth(month, options);
		if (nrOfMonthWeeks < 6) {
			const lastWeek = weeksInMonth[weeksInMonth.length - 1];
			const lastDate = lastWeek.dates[lastWeek.dates.length - 1];
			const toDate = addWeeks(lastDate, 6 - nrOfMonthWeeks);
			const extraWeeks = daysToMonthWeeks(
				addWeeks(lastDate, 1),
				toDate,
				options,
			);
			weeksInMonth.push(...extraWeeks);
		}
	}
	return weeksInMonth;
}
